import type { ItemFieldRow } from './item-field-reader';
import type { FormulaReadDependencies } from './formula-read-context';
import { createFormulaReadContext } from './formula-read-context';
import { readItemScalarLayerSlot } from './layer-cell-readers';
import { phpJsonInteger } from './php-integer-arithmetic';

export interface SkillRequirementDependencies extends FormulaReadDependencies {
    skillEntries: readonly {item_id: string; variable_name: string}[];
    layerEntries?: (row: ItemFieldRow | null, key: string) => readonly unknown[];
}
const arrayLike = (v: unknown): v is ItemFieldRow => v !== null && typeof v === 'object';
const own = (v: unknown, key: string): unknown => arrayLike(v) && Object.prototype.hasOwnProperty.call(v, key) ? (v as Record<string, unknown>)[key] : undefined;
const arrayArg = (v: unknown): ItemFieldRow => { if (!arrayLike(v)) throw new TypeError('Skill requirements require a PHP array'); return v; };
const valid = (v: string): boolean => /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(v);
/** Native check_skill_requirements and caster_skills. Read the evolving carrier
 * on every call; compiled skills take precedence over the legacy skills slot.
 */
export function createSkillRequirementChecker(deps: SkillRequirementDependencies) {
    const entries = deps.layerEntries ?? createFormulaReadContext(deps).layerEntries;
    function skillRequirements(input: ItemFieldRow, caster: ItemFieldRow): boolean {
        const ability = arrayArg(input), host = arrayArg(caster);
        if (!Object.keys(ability).length) return true;
        const skills = arrayArg(own(own(own(host, 'computed'), 'creature'), 'skills') ?? own(host, 'skills') ?? []);
        const registry = new Map(deps.skillEntries.map(row => [row.item_id, row.variable_name]));
        const layers = arrayLike(own(ability, 'item_layer_types')) ? own(ability, 'item_layer_types') as ItemFieldRow : ability;
        for (const key of Object.keys(layers)) {
            if (!key.startsWith('requires_skill_tier_')) continue;
            const required = phpJsonInteger(key.split('requires_skill_tier_').join('')); let met = false;
            for (const entry of Object.values(entries(ability, key))) {
                const id = readItemScalarLayerSlot(entry); if (typeof id !== 'string' || !valid(id)) continue;
                const variable = registry.get(id);
                if (variable !== undefined && phpJsonInteger(own(own(skills, variable), 'tier_level') ?? 0) >= required) { met = true; break; }
            }
            if (!met) return false;
        }
        return true;
    }
    return skillRequirements;
}
